import { ColDef, ColGroupDef, ValueFormatterParams } from '@ag-grid-community/core';
import { formatDate } from '@angular/common';
import dayjs from 'dayjs';

import { ZERO_HYPHEN } from '@shared/constants/format';
import { Utils } from '@shared/utils/utils';

import { GridConfigOptions } from './grid-config-context';
import { AdjustmentsToPrepaidRow } from '../adjustments-to-prepaid.model';

// Helper function to create column definition
const createColumnDef = (
  field: keyof AdjustmentsToPrepaidRow,
  headerName: string,
  options: GridConfigOptions,
  additionalProps: Partial<ColDef<AdjustmentsToPrepaidRow>> = {}
): ColDef<AdjustmentsToPrepaidRow> => {
  const { getCellClasses } = options;

  return {
    cellClass: getCellClasses(),
    editable: false,
    field,
    headerName,
    width: 120,
    ...additionalProps,
  };
};

const createDateColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { getCellClasses } = options;

  return createColumnDef('create_date', 'Created at', options, {
    cellClass: getCellClasses(['text-right']),
    editable: false,
    filterParams: {
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ? dayjs(params.value).format('MM/DD/YYYY') : ZERO_HYPHEN;
      },
    },
    headerTooltip: 'Created at',
    minWidth: 150,
    suppressSizeToFit: true,
    valueFormatter: (params) => {
      if (!params.value) return '';

      return formatDate(params.value, 'MM/dd/yyyy', 'en-US');
    },
    width: 150,
  });
};

const createAuthorColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { getAuthor, isAdmin } = options;

  return createColumnDef('created_by', 'Created by', options, {
    editable: false,
    filterParams: {
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return ZERO_HYPHEN;

        const author = getAuthor(params.value);

        return author ? Utils.agUserFormatter(author, isAdmin) : ZERO_HYPHEN;
      },
    },
    headerTooltip: 'Created by',
    minWidth: 150,
    suppressFillHandle: true,
    suppressSizeToFit: true,
    tooltipValueGetter: (params) => params.valueFormatted,
    valueFormatter: (params) => {
      if (!params.value) return '';

      const author = getAuthor(params.value);

      if (!author) return '';

      return Utils.agUserFormatter(author, isAdmin);
    },
    width: 150,
  });
};

export const createdColumnGroup = (
  options: GridConfigOptions
): (ColDef<AdjustmentsToPrepaidRow> | ColGroupDef<AdjustmentsToPrepaidRow>)[] => {
  return [
    {
      children: [createDateColumn(options), createAuthorColumn(options)],
      headerClass: ['header-marker', 'text-center'],
      headerName: 'Created',
      suppressStickyLabel: true,
    },
  ];
};
